import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { WaggleMark } from "@/components/waggle/WaggleLogo";

const GIS_SRC = "https://accounts.google.com/gsi/client";
// GIS accepts button widths from 200 to 400px; 384px is the form column on desktop
const GOOGLE_MIN_WIDTH = 200;
const GOOGLE_MAX_WIDTH = 400;

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (r: { credential: string }) => void }) => void;
          renderButton: (el: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

function loadGoogleScript(): Promise<void> {
  if (window.google?.accounts) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    const script = existing ?? document.createElement("script");
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => reject(new Error("Failed to load Google sign-in")));
    if (!existing) {
      script.src = GIS_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  });
}

export default function Login() {
  const [, setLocation] = useLocation();
  const { login, loginWithGoogle, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // If a user is already authenticated when arriving here, redirect to dashboard
  useEffect(() => {
    if (user) {
      setLocation("/dashboard");
    }
  }, [user, setLocation]);

  const [googleClientId, setGoogleClientId] = useState<string | null>(null);
  const [googleReady, setGoogleReady] = useState(false);
  const [googleWidth, setGoogleWidth] = useState(384);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // The client ID comes from the server at runtime; no ID means no Google button
  useEffect(() => {
    fetch("/api/config")
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => setGoogleClientId(cfg?.googleClientId ?? null))
      .catch(() => setGoogleClientId(null));
  }, []);

  // GIS renders a fixed-pixel iframe, so track the wrapper width and re-render to match the form
  useEffect(() => {
    const el = googleButtonRef.current;
    if (!googleClientId || !el) return;
    let timer: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const width = Math.round(el.clientWidth);
        if (width > 0) setGoogleWidth(width);
      }, 100);
    });
    observer.observe(el);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [googleClientId]);

  useEffect(() => {
    if (!googleClientId) return;
    let cancelled = false;
    loadGoogleScript()
      .then(() => {
        if (cancelled || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: async ({ credential }) => {
            setError("");
            setIsLoading(true);
            try {
              await loginWithGoogle(credential);
              window.location.href = "/dashboard";
            } catch (err: any) {
              setError(err.message || "Google sign-in failed");
              setIsLoading(false);
            }
          },
        });
        setGoogleReady(true);
      })
      .catch(() => {
        // Script blocked or offline: password login still works
      });
    return () => {
      cancelled = true;
    };
  }, [googleClientId, loginWithGoogle]);

  useEffect(() => {
    const el = googleButtonRef.current;
    if (!googleReady || !window.google || !el) return;
    el.replaceChildren();
    window.google.accounts.id.renderButton(el, {
      theme: "outline",
      size: "large",
      width: Math.min(Math.max(googleWidth, GOOGLE_MIN_WIDTH), GOOGLE_MAX_WIDTH),
      text: "signin_with",
    });
  }, [googleReady, googleWidth]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await login(email, password);
      // Hard redirect avoids race conditions with React context propagation —
      // the new page load lets AuthProvider rehydrate from localStorage cleanly.
      window.location.href = "/dashboard";
    } catch (err: any) {
      setError(err.message || "Invalid email or password");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-brand-50 to-background flex items-center justify-center p-6">
      <Card className="w-full max-w-md rounded-2xl shadow-lg">
        <CardHeader className="text-center space-y-4 pt-8">
          <div className="flex justify-center">
            <WaggleMark className="h-14 w-14" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-foreground">Waggle</h1>
            <p className="text-muted-foreground mt-2">Turn what you've taught into a ready-to-print test</p>
          </div>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="input-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="input-password"
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              size="lg"
              data-testid="button-login"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Login"}
            </Button>
          </form>

          {googleClientId && (
            <div className="mt-6" data-testid="google-signin">
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>
              <div ref={googleButtonRef} className="mx-auto w-full max-w-[400px]" />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
